import React, { useState, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// 定义背景音乐类型
export interface BgMusic {
  url: string;
  name: string;
  type: string;
  duration: number;
}

// 定义BgMusicSelector的属性接口
interface BgMusicSelectorProps {
  bgMusicList: BgMusic[];
  selectedBgMusic: string | null;
  bgMusicVolume: number;
  onBgMusicSelect: (musicUrl: string | null) => void;
  onVolumeChange: (volume: number) => void;
}

const BgMusicSelector: React.FC<BgMusicSelectorProps> = ({
  bgMusicList,
  selectedBgMusic,
  bgMusicVolume,
  onBgMusicSelect,
  onVolumeChange
}) => {
  // 音频播放状态管理
  const [currentPlayingMusic, setCurrentPlayingMusic] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timeUpdateRef = useRef<number | null>(null);
  const { t } = useTranslation();

  // 组件卸载时清理定时器和音频
  React.useEffect(() => {
    return () => {
      // 清除定时器
      if (timeUpdateRef.current) {
        clearInterval(timeUpdateRef.current);
        timeUpdateRef.current = null;
      }
      // 停止音频播放
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  // 播放音频
  const playAudio = (musicUrl: string) => {
    // 停止当前播放的音频
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // 清除之前的定时器
    if (timeUpdateRef.current) {
      clearInterval(timeUpdateRef.current);
      timeUpdateRef.current = null;
    }

    // 重置当前时间
    setCurrentTime(0);

    // 创建新的音频实例
    const audio = new Audio(musicUrl.trim());
    audio.volume = bgMusicVolume;
    audioRef.current = audio;

    // 设置播放状态
    setCurrentPlayingMusic(musicUrl);
    setIsPlaying(true);

    // 启动时间更新定时器
    timeUpdateRef.current = window.setInterval(() => {
      if (audioRef.current) {
        setCurrentTime(audioRef.current.currentTime);
      }
    }, 1000);

    // 播放结束时重置状态
    audio.onended = () => {
      setIsPlaying(false);
      setCurrentPlayingMusic(null);
      setCurrentTime(0);
      if (timeUpdateRef.current) {
        clearInterval(timeUpdateRef.current);
        timeUpdateRef.current = null;
      }
    };

    // 播放音频
    audio.play().catch(error => {
      console.error('播放音频失败:', error);
      setIsPlaying(false);
      setCurrentPlayingMusic(null);
      setCurrentTime(0);
      if (timeUpdateRef.current) {
        clearInterval(timeUpdateRef.current);
        timeUpdateRef.current = null;
      }
    });
  };

  // 停止音频
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    // 清除定时器
    if (timeUpdateRef.current) {
      clearInterval(timeUpdateRef.current);
      timeUpdateRef.current = null;
    }
    setIsPlaying(false);
    setCurrentPlayingMusic(null);
    setCurrentTime(0);
  };

  // 音量变化处理
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const volume = parseFloat(e.target.value);
    onVolumeChange(volume);
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  };

  // 格式化时长
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-4 mb-4 transition-all duration-300 opacity-100">
      {/* 标题 */}
      <h3 className="text-sm font-medium text-white mb-3">{t('inputBar.bgMusic.title')}</h3>

      {/* 音乐列表 */}
      <div className="bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-lg p-2 max-h-60 overflow-y-auto custom-scrollbar">
        {bgMusicList.length > 0 ? (
          bgMusicList.map(music => (
            <div
              key={music.url}
              className={`flex items-center justify-between py-2 px-3 rounded-lg mb-2 transition-colors duration-200 ${selectedBgMusic === music.url ? 'border border-blue-500/50' : 'hover:bg-gray-700/50 cursor-pointer'} active:bg-transparent active:outline-none active:border-transparent focus:bg-transparent focus:outline-none focus:border-transparent focus-visible:bg-transparent focus-visible:outline-none focus-visible:border-transparent -webkit-tap-highlight-color: transparent`}
              onClick={() => onBgMusicSelect(music.url)}
            >
              {/* 左侧：音乐名称和时长 */}
              <div className="flex-1 text-white text-sm">
                <div>{music.name}</div>
                <div className="text-xs text-gray-400">{formatDuration(music.duration)}</div>
              </div>

              {/* 右侧：播放时间和试听按钮 */}
              <div className="flex items-center space-x-2">
                {/* 播放时间 */}
                {currentPlayingMusic === music.url && isPlaying && (
                  <span className="text-xs text-white min-w-[40px] text-right">
                    {formatDuration(currentTime)}
                  </span>
                )}
                {/* 试听按钮 */}
                <button
                  className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${currentPlayingMusic === music.url && isPlaying ? 'bg-pink-500/50 hover:bg-pink-500/70' : 'bg-gray-700/50 hover:bg-gray-600/50'}`}
                  onClick={(e) => {
                    e.stopPropagation(); // 防止触发选择事件
                    if (currentPlayingMusic === music.url && isPlaying) {
                      stopAudio();
                    } else {
                      playAudio(music.url);
                    }
                  }}
                >
                  {currentPlayingMusic === music.url && isPlaying ? (
                    <Pause className="w-4 h-4 text-white" />
                  ) : (
                    <Play className="w-4 h-4 text-white" />
                  )}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="p-3 text-gray-400 text-center">
            {t('inputBar.bgMusic.loading')}
          </div>
        )}
      </div>

      {/* 音量控制 */}
      <div className="bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-lg p-3 mt-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-300">{t('inputBar.bgMusic.volume')}</label>
          <button
            className="text-gray-300 hover:text-white transition-colors"
            onClick={() => onVolumeChange(bgMusicVolume > 0 ? 0 : 0.5)}
          >
            {bgMusicVolume > 0 ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">0</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={bgMusicVolume}
            onChange={handleVolumeChange}
            className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
          <span className="text-xs text-gray-400">1</span>
          <span className="text-sm font-medium text-blue-400 w-12 text-right">{Math.round(bgMusicVolume * 100)}%</span>
        </div>
      </div>
    </div>
  );
};

export default BgMusicSelector;